// RelayTooltip — desktop hover tooltip showing a relay's income, owner,
// connection state, and active disruptions (jam / cut / fortify /
// disrupt). Touch input never fires hover, so this is desktop-only;
// the panel is hidden if the latest pointer event was a touch.
//
// Lifecycle: GameScene constructs the tooltip and attaches it via a
// per-frame poller (worldCursor + latest snapshot) so we don't need a
// separate event subscription on Phaser pointermove (InputRouter
// already updates worldCursor every move).

import type { ControlNode, MatchState, PlayerId } from '@signalclash/shared';

import { injectHudThemeStyles } from './hudThemeStyles.js';

const ROOT_ID = 'relay-tooltip';
const STYLE_ID = 'relay-tooltip-styles';
// Hit radius for hover detection — slightly wider than the click
// picker since we want to *show* info as the cursor approaches.
const HOVER_RADIUS_PX = 60;

export class RelayTooltip {
  private root: HTMLDivElement | null = null;
  private titleEl: HTMLDivElement;
  private bodyEl: HTMLDivElement;
  private viewerId: PlayerId;
  private currentNodeId: number | null = null;
  private isTouch = false;
  private hidden = true;

  constructor(viewerId: PlayerId) {
    this.viewerId = viewerId;
    injectStyles();
    // V1.8 — drop any orphan from a prior lifecycle whose dispose() may
    // have thrown. Without this, ensureRoot() finds the stale node, we
    // append a fresh title/body inside it, and the next update() races
    // between detached state and the new children.
    const orphan = document.getElementById(ROOT_ID);
    if (orphan !== null) orphan.remove();
    this.root = ensureRoot();
    this.titleEl = document.createElement('div');
    this.titleEl.className = 'relay-tooltip__title';
    this.bodyEl = document.createElement('div');
    this.bodyEl.className = 'relay-tooltip__body';
    this.root.appendChild(this.titleEl);
    this.root.appendChild(this.bodyEl);
    this.root.style.display = 'none';
  }

  setViewerId(viewerId: PlayerId): void {
    this.viewerId = viewerId;
  }

  setTouchInput(touch: boolean): void {
    this.isTouch = touch;
    if (touch) this.hide();
  }

  // Called per-frame from GameScene with the latest world cursor and
  // snapshot. If a relay is within HOVER_RADIUS_PX we show; otherwise hide.
  update(snap: MatchState | null, worldX: number, worldY: number, screenX: number, screenY: number): void {
    if (this.isTouch || snap === null || this.root === null) {
      this.hide();
      return;
    }
    let best: ControlNode | null = null;
    let bestDist = HOVER_RADIUS_PX;
    for (const node of Object.values(snap.controlNodes)) {
      const d = Math.hypot(node.pos.x - worldX, node.pos.y - worldY);
      if (d < bestDist) {
        bestDist = d;
        best = node;
      }
    }
    if (best === null) {
      this.hide();
      return;
    }
    this.showFor(best, snap, screenX, screenY);
  }

  private showFor(node: ControlNode, snap: MatchState, screenX: number, screenY: number): void {
    if (this.root === null) return;
    const ownerLabel = node.ownerId === null
      ? 'Neutral'
      : node.ownerId === this.viewerId
        ? 'You'
        : 'Enemy';
    this.titleEl.textContent = `Relay — ${ownerLabel}`;
    this.titleEl.className = `relay-tooltip__title relay-tooltip__title--${ownerLabel.toLowerCase()}`;

    const lines: string[] = [];
    if (node.ownerId !== null) {
      // generatesSignal is per-second income (added directly to the
      // per-tick rate in EconomySystem). The previous "/ 5s" label was
      // a unit bug — left players underestimating relay value by 5×.
      const isolated = !node.isConnected;
      lines.push(
        isolated
          ? `Income: +${node.generatesSignal}/s (isolated — reduced)`
          : `Income: +${node.generatesSignal}/s`,
      );
      lines.push(`Connected: ${node.isConnected ? 'yes' : 'no — isolated'}`);
    } else {
      lines.push('Capture to gain +3/s signal income.');
    }
    if (node.contested) lines.push('CONTESTED — both sides present');
    if (node.linkCutUntilTick > snap.tick) lines.push('Signal link cut');
    if (node.jammedUntilTick > snap.tick) lines.push('Jammed — vision degraded');
    if (node.disruptedUntilTick > snap.tick) lines.push('Uplink disrupted');
    if (node.fortifiedUntilTick > snap.tick) lines.push('Fortified — capture resistance ↑');
    if (node.guardedBy === 'neutral_camp') lines.push('Guarded by neutrals');

    this.bodyEl.innerHTML = '';
    for (const l of lines) {
      const row = document.createElement('div');
      row.className = 'relay-tooltip__row';
      row.textContent = l;
      this.bodyEl.appendChild(row);
    }

    // Position the tooltip just below the cursor with edge clamping.
    const PADDING = 14;
    const root = this.root;
    root.style.display = 'block';
    this.hidden = false;
    const rect = root.getBoundingClientRect();
    const w = rect.width || 220;
    const h = rect.height || 90;
    let x = screenX + 18;
    let y = screenY + 18;
    if (x + w + PADDING > window.innerWidth) x = screenX - w - 18;
    if (y + h + PADDING > window.innerHeight) y = screenY - h - 18;
    root.style.left = `${Math.max(PADDING, x)}px`;
    root.style.top = `${Math.max(PADDING, y)}px`;
    this.currentNodeId = node.id;
  }

  hide(): void {
    if (this.hidden) return;
    if (this.root !== null) this.root.style.display = 'none';
    this.hidden = true;
    this.currentNodeId = null;
  }

  dispose(): void {
    if (this.root !== null) this.root.remove();
    this.root = null;
    // Belt-and-braces: if root.remove() was a no-op because root was
    // already detached, ensure no orphan with our id survives in the DOM.
    const stray = typeof document !== 'undefined' ? document.getElementById(ROOT_ID) : null;
    if (stray !== null) stray.remove();
  }
}

function ensureRoot(): HTMLDivElement {
  const existing = document.getElementById(ROOT_ID);
  if (existing instanceof HTMLDivElement) return existing;
  const div = document.createElement('div');
  div.id = ROOT_ID;
  document.body.appendChild(div);
  return div;
}

function injectStyles(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
#${ROOT_ID} {
  position: fixed;
  z-index: 30;
  pointer-events: none;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  background: rgba(12, 14, 18, 0.94);
  border: 1px solid rgba(160, 180, 200, 0.32);
  border-radius: 4px;
  padding: 8px 12px;
  min-width: 180px;
  max-width: 280px;
  font-size: 11px;
  letter-spacing: 0.03em;
  color: #d4dae2;
  box-shadow: 0 6px 22px rgba(0, 0, 0, 0.55);
}
.relay-tooltip__title {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  margin-bottom: 4px;
}
.relay-tooltip__title--you      { color: #6fb8ff; }
.relay-tooltip__title--enemy    { color: #ff8c64; }
.relay-tooltip__title--neutral  { color: #c4ccd6; }
.relay-tooltip__body { display: flex; flex-direction: column; gap: 2px; }
.relay-tooltip__row { color: #aeb6c0; }
  `;
  document.head.appendChild(style);
  injectHudThemeStyles();
}
